import './dashboard.css';
import './gallery.css';
import { useRef, useState } from 'react';
import { Route, Routes } from 'react-router';
import ApprovalPage from './pages/ApprovalPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AttendancePage from './pages/AttendancePage';
import ComponentsGallery from './pages/ComponentsGallery';
import ExamplesDashboard from './pages/ExamplesDashboard';
import IndexPage from './pages/IndexPage';
import NotFoundPage from './pages/NotFoundPage';
import PayrollPage from './pages/PayrollPage';
import PeoplePage from './pages/PeoplePage';
import RecruitPage from './pages/RecruitPage';
import SettingsPage from './pages/SettingsPage';
import ThemesGallery from './pages/ThemesGallery';
import TokensGallery from './pages/TokensGallery';
import WorkbenchPage from './pages/WorkbenchPage';
import AxiDarkModeToggle from './components/AxiDarkModeToggle';
import AxiSettingsPanel from './components/AxiSettingsPanel';
import AxiMobileMenuToggle from './components/AxiMobileMenuToggle';
import { ThemeProvider, resolveInitialThemeSlug, themeRegistry } from './themes';
import {
  cssVarsToRule,
  tokensToCssVars,
  tokensWithOverrides
} from './theme';
import type { AtelierStats, LedgerEntry } from './pages/IndexPage';

/* react-router v7 (library mode) is intentionally pinned to ^7.18.1; v8 is
 * the npm `latest` but introduces minor-version churn risk. Upgrade path
 * is API-compatible with v8 when needed.
 * The router context lives in `main.tsx` (BrowserRouter in production, an
 * arbitrary MemoryRouter/HistoryRouter in tests) so App can be rendered
 * under any location without reaching for `window.document`.
 *
 * App is a thin shell: it owns the theme <style> tags and the route
 * table. Three categories of routes:
 *   1. Design-system gallery at `/`, `/examples/{tokens,themes,components}`.
 *      These pages render inside the gallery shell with no sidebar.
 *   2. Workbench demo at `/examples/dashboard` (legacy URL kept for
 *      backward compatibility) and the canonical `/app/dashboard`.
 *      Both render `WorkbenchPage` with the dark-gold dashboard chrome.
 *   3. Business pages at `/app/{people,attendance,payroll,recruit,
 *      approval,analytics,settings}`. Each renders inside
 *      `<DashboardLayout>` with `activeSidebarId` set so the sidebar
 *      pill follows the current page.
 *
 * The settings-panel gear toggle and the mobile-menu hamburger overlay
 * are constructed at the App level (state lives here) and threaded
 * down to every page that needs them via props.
 */

/* Atelier feed — computed once at module scope so re-renders don't
 * re-enumerate. `import.meta.glob` is Vite-native; it returns an object
 * keyed by module path. We count the keys to surface a live component
 * total on the homepage's atelier row. Keep in sync with src/components/
 * when adding or removing top-level component files. */
const COMPONENT_GLOB = import.meta.glob('./components/*.tsx');
const ATELIER: AtelierStats = {
  components: Object.keys(COMPONENT_GLOB).length,
  themes: themeRegistry.list().length,
  // Routes are enumerated statically below; the count must match the
  // <Route> declarations further down. If you add a route, update this.
  routes: 13
};

/* Ledger feed — top-3 entries from CHANGE.md, captured at module scope.
 * Kept as a static array rather than a runtime fetch so tests can render
 * IndexPage without `fetch` / `import.meta.glob('../CHANGE.md?raw')`
 * dance. When the next three top-level CHANGE.md entries shift, refresh
 * this array in the same commit. */
const LEDGER: LedgerEntry[] = [
  { date: '2026-07-10', title: 'Zero-context handoff manifest (v2) + VERIFICATION.md', hash: 'af86735' },
  { date: '2026-07-12', title: 'Initialize 8 business pages backed by mock data', hash: 'fbee444' },
  { date: '2026-07-12', title: 'Realign positioning: / becomes the design-system gallery', hash: 'dbbdfc3' }
];

function App() {
  const baseRule = cssVarsToRule(tokensToCssVars());
  const activeTheme = resolveInitialThemeSlug();
  const themedRule = cssVarsToRule(
    tokensWithOverrides(tokensToCssVars(), activeTheme.tokens)
  );

  const [panelOpen, setPanelOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const hamburgerRef = useRef<HTMLButtonElement | null>(null);

  const settingsPanel = (
    <>
      <AxiDarkModeToggle
        open={panelOpen}
        onToggle={() => setPanelOpen((v) => !v)}
        buttonRef={triggerRef}
      />
      <AxiSettingsPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        triggerRef={triggerRef}
      />
    </>
  );

  const mobileMenu = (
    <AxiMobileMenuToggle
      open={mobileMenuOpen}
      onToggle={() => setMobileMenuOpen((v) => !v)}
      onClose={() => setMobileMenuOpen(false)}
      triggerRef={hamburgerRef}
    />
  );

  // Common chrome props for every page that uses `<DashboardLayout>`.
  const chrome = {
    settingsPanel,
    mobileMenu,
    sidebarOpen: mobileMenuOpen
  };

  return (
    <ThemeProvider initialTheme={activeTheme}>
      <style data-axi-tokens>{baseRule}</style>
      <style data-axi-theme={activeTheme.slug}>{themedRule}</style>
      <Routes>
        {/* Design-system gallery */}
        <Route path="/" element={<IndexPage themeSlug={activeTheme.slug} atelier={ATELIER} ledger={LEDGER} />} />
        <Route path="/examples/tokens" element={<TokensGallery />} />
        <Route path="/examples/themes" element={<ThemesGallery />} />
        <Route path="/examples/components" element={<ComponentsGallery />} />
        <Route path="/examples/dashboard" element={<ExamplesDashboard {...chrome} />} />

        {/* Business pages */}
        <Route path="/app/dashboard" element={<WorkbenchPage {...chrome} />} />
        <Route path="/app/people" element={<PeoplePage {...chrome} />} />
        <Route path="/app/attendance" element={<AttendancePage {...chrome} />} />
        <Route path="/app/payroll" element={<PayrollPage {...chrome} />} />
        <Route path="/app/recruit" element={<RecruitPage {...chrome} />} />
        <Route path="/app/approval" element={<ApprovalPage {...chrome} />} />
        <Route path="/app/analytics" element={<AnalyticsPage {...chrome} />} />
        <Route path="/app/settings" element={<SettingsPage {...chrome} />} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </ThemeProvider>
  );
}

export default App;